import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, User, Home, Radio, LogOut, Sparkles, Store, ArrowLeft, Film, Bell, CheckCheck, Shield, Package, HelpCircle, Rocket, Calendar } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { useCart } from "../lib/CartContext";
import { useNotifications } from "../lib/NotificationContext";

export function TopBar({ onSearchClick }) {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { items: notifs, unread, markRead, markAllRead } = useNotifications();
  const [bellOpen, setBellOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const bellRef = useRef(null);
  const loc = useLocation();
  const nav = useNavigate();

  // Close menus on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, []);

  // Close menus on route change
  useEffect(() => { setUserMenuOpen(false); setBellOpen(false); }, [loc.pathname]);
  const showBack = loc.pathname !== "/" && loc.pathname !== "/login" && loc.pathname !== "/register";

  return (
    <header className="sticky top-0 z-40 glass border-b border-[#3D2845]">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between gap-2 sm:gap-3">
        <div className="flex items-center gap-2 shrink-0">
          {showBack && (
            <button
              onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
              data-testid="top-back-btn"
              className="w-10 h-10 rounded-full bg-[#261534] border border-[#3D2845] hover:border-[#D4FF00] hover:text-[#D4FF00] flex items-center justify-center active:scale-95 transition"
              aria-label="Back"
            >
              <ArrowLeft size={18} strokeWidth={2.5}/>
            </button>
          )}
          <Link to="/" data-testid="top-logo" className="flex items-center gap-2 group">
            <img
              src="https://customer-assets.emergentagent.com/job_live-commerce-hub-4/artifacts/29xleaxd_ChatGPT%20Image%20May%2014%2C%202026%2C%2012_57_41%20PM.png"
              alt="ShopRizz"
              className="h-10 w-auto active:scale-95 transition drop-shadow-[0_0_12px_rgba(212,255,0,0.15)]"
            />
          </Link>
        </div>

        <button
          onClick={onSearchClick}
          data-testid="search-open-btn"
          className="sm:flex-1 sm:max-w-xl flex items-center gap-3 px-3 sm:px-4 py-2.5 rounded-full bg-[#261534] border border-[#3D2845] hover:border-[#D4FF00] transition text-left text-[#A1A1AA]"
          aria-label="Search"
        >
          <Search size={18} strokeWidth={2.5} />
          <span className="hidden sm:inline text-sm flex-1 truncate">Search products, stores, streams…</span>
          <span className="hidden sm:inline ml-auto text-[10px] font-mono-display border border-[#3D2845] px-2 py-0.5 rounded">⌘K</span>
        </button>

        <div className="flex items-center gap-2">
          <CountryHeaderPicker/>
          {user && (
            <div className="relative" ref={bellRef}>
              <button
                onClick={() => { setBellOpen(b => !b); setUserMenuOpen(false); }}
                data-testid="notif-bell-btn"
                className="relative p-2.5 rounded-full bg-[#261534] border border-[#3D2845] hover:border-[#D4FF00] transition"
                aria-label="Notifications"
              >
                <Bell size={18} strokeWidth={2.5}/>
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 bg-[#FF007F] text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center" data-testid="notif-unread-badge">{unread > 9 ? "9+" : unread}</span>
                )}
              </button>
              {bellOpen && (
                <div className="absolute right-0 top-full mt-2 w-[340px] bg-[#261534] border border-[#3D2845] rounded-2xl z-50 shadow-2xl overflow-hidden" data-testid="notif-dropdown">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[#3D2845]">
                    <span className="font-display font-bold uppercase text-sm">Notifications</span>
                    {unread > 0 && (
                      <button onClick={markAllRead} data-testid="notif-mark-all-read" className="text-xs text-[#A1A1AA] hover:text-[#D4FF00] flex items-center gap-1">
                        <CheckCheck size={12}/> Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-[60vh] overflow-y-auto">
                    {notifs.length === 0 && <div className="p-6 text-center text-[#A1A1AA] text-sm">No notifications yet.</div>}
                    {notifs.map(n => (
                      <Link
                        key={n.notif_id}
                        to={n.link || "/"}
                        data-testid={`notif-item-${n.notif_id}`}
                        onClick={() => { markRead(n.notif_id); setBellOpen(false); }}
                        className={`block px-4 py-3 border-b border-[#3D2845] hover:bg-[#3D2845] transition ${!n.read ? "bg-[#D4FF00]/5" : ""}`}
                      >
                        <div className="flex items-start gap-2">
                          {!n.read && <span className="w-2 h-2 rounded-full bg-[#FF007F] mt-1.5 shrink-0"/>}
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm">{n.title}</div>
                            <div className="text-xs text-[#A1A1AA] line-clamp-2">{n.body}</div>
                            <div className="text-[10px] text-[#A1A1AA] mt-1 font-mono-display">{new Date(n.created_at).toLocaleString()}</div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <Link to="/rentals" data-testid="top-rentals-link"
            title="Rent designer outfits & jewellery"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#261534] border border-[#3D2845] hover:border-[#00F0FF] hover:text-[#00F0FF] transition text-[#A1A1AA] text-xs font-bold uppercase tracking-widest">
            <Calendar size={14}/>Rent
          </Link>
          <Link to="/cart" data-testid="top-cart-link" className="relative p-2.5 rounded-full bg-[#261534] border border-[#3D2845] hover:border-[#D4FF00] transition">
            <ShoppingBag size={18} strokeWidth={2.5} />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#FF007F] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{count}</span>
            )}
          </Link>
          {user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                data-testid="user-menu-btn"
                onClick={() => { setUserMenuOpen(o => !o); setBellOpen(false); }}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                className="flex items-center gap-2 px-2 py-1.5 rounded-full border border-[#3D2845] hover:border-[#D4FF00] active:scale-95 transition"
              >
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                <span className="hidden md:block text-sm font-semibold">{user.name?.split(" ")[0]}</span>
              </button>
              {userMenuOpen && (
                <div
                  data-testid="user-menu-dropdown"
                  className="absolute right-0 top-full mt-2 bg-[#261534] border border-[#3D2845] rounded-xl min-w-[220px] py-2 z-50 shadow-2xl"
                >
                  <Link to="/profile" data-testid="nav-profile" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#3D2845]">
                    <User size={14}/> My profile
                  </Link>
                  {/* "My orders" is a shopper-side feature — only buyers
                      see it. Sellers manage their orders from the Seller
                      dashboard → Orders tab; admins don't shop here. */}
                  {user.role === "buyer" && (
                    <Link to="/orders" data-testid="nav-orders" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#3D2845]">
                      <Package size={14}/> My orders
                    </Link>
                  )}
                  {user.role === "buyer" && (
                    <Link to="/my-rentals" data-testid="nav-my-rentals" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#3D2845]">
                      <Calendar size={14}/> My rentals
                    </Link>
                  )}
                  {user.role === "seller" && (
                    <Link to="/seller" data-testid="nav-seller" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#3D2845]">
                      <Store size={14}/> Seller dashboard
                    </Link>
                  )}
                  {user.role === "seller" && (
                    <>
                      <Link to="/studio" data-testid="nav-studio" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#3D2845] group">
                        <span className="w-5 h-5 rounded-md flex items-center justify-center bg-[#D4FF00]/20 group-hover:bg-[#D4FF00]/40 transition">
                          <Film size={12} className="text-[#D4FF00]"/>
                        </span>
                        Studio
                        <span className="ml-auto text-[9px] font-bold uppercase tracking-widest text-[#A1A1AA] group-hover:text-[#D4FF00]">record</span>
                      </Link>
                      <Link to="/go-live" data-testid="nav-go-live" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#3D2845] group">
                        <span className="w-5 h-5 rounded-md flex items-center justify-center bg-[#FF007F]/20 group-hover:bg-[#FF007F]/40 transition">
                          <Radio size={12} className="text-[#FF007F]"/>
                        </span>
                        Go Live
                        <span className="ml-auto text-[9px] font-bold uppercase tracking-widest text-[#FF007F] opacity-80">broadcast</span>
                      </Link>
                    </>
                  )}
                  {user.role === "admin" && (
                    <Link to="/admin" data-testid="nav-admin" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#3D2845]">
                      <Shield size={14}/> Admin console
                    </Link>
                  )}
                  <div className="border-t border-[#3D2845] my-1"/>
                  <Link to="/faq" data-testid="nav-faq" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-[#3D2845]">
                    <HelpCircle size={14}/> Help & FAQ
                  </Link>
                  <div className="border-t border-[#3D2845] my-1"/>
                  <button data-testid="logout-btn" onClick={logout} className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#3D2845] flex items-center gap-2 text-[#FF007F]">
                    <LogOut size={14}/>Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/faq" data-testid="top-faq-link"
                title="Help & FAQ"
                className="hidden sm:inline-flex p-2.5 rounded-full bg-[#261534] border border-[#3D2845] hover:border-[#D4FF00] transition text-[#A1A1AA] hover:text-[#D4FF00]">
                <HelpCircle size={18}/>
              </Link>
              <Link to="/login" data-testid="top-login-link" className="btn-primary !px-3 !py-2 sm:!px-4 text-xs sm:text-sm">Login</Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export function BottomNav() {
  const loc = useLocation();
  // Hide on full-immersion routes (live shows + tiktok-style /videos feed)
  // so the swipe screen owns the entire viewport on mobile.
  if (loc.pathname.startsWith("/live/") ||
      loc.pathname === "/videos" || loc.pathname.startsWith("/videos/")) {
    return null;
  }
  const is = (p) => loc.pathname === p || (p !== "/" && loc.pathname.startsWith(p));
  const Item = ({ to, icon: Icon, label, testid }) => (
    <Link to={to} data-testid={testid}
      className={`flex flex-col items-center justify-center gap-1 flex-1 min-w-0 px-0.5 py-2 ${is(to) ? "text-[#D4FF00]" : "text-[#A1A1AA]"}`}>
      <Icon size={20} strokeWidth={2.5}/>
      <span className="w-full text-center text-[9px] font-bold uppercase tracking-tight leading-none whitespace-nowrap overflow-hidden text-ellipsis">{label}</span>
    </Link>
  );
  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t border-[#3D2845] pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-stretch">
        <Item to="/" icon={Home} label="Home" testid="bn-home"/>
        <Item to="/live" icon={Radio} label="Live" testid="bn-live"/>
        <Item to="/new-launches" icon={Rocket} label="Launches" testid="bn-launches"/>
        <Item to="/videos" icon={Film} label="Videos" testid="bn-videos"/>
        <Item to="/cart" icon={ShoppingBag} label="Cart" testid="bn-cart"/>
        <Item to="/profile" icon={User} label="Me" testid="bn-me"/>
      </div>
    </nav>
  );
}

export function Marquee({ text = "LIVE NOW · NEW DROPS · TRENDING · FREE SHIPPING · 24/7 SHOW" }) {
  return (
    <div className="overflow-hidden bg-[#D4FF00] border-y-2 border-black py-2">
      <div className="marquee-track">
        {Array.from({length: 4}).map((_, i) => (
          <span key={`marquee-rep-${i}`} className="font-display font-black text-black text-sm uppercase tracking-wider px-8">
            ★ {text}
          </span>
        ))}
      </div>
    </div>
  );
}

export function LiveBadge({ size = "sm" }) {
  return (
    <span className="live-badge">
      <span className="dot"/> LIVE
    </span>
  );
}


/**
 * CountryHeaderPicker — small flag button that opens a searchable country
 * list. Persists the choice to localStorage under `buyer_country`; the
 * axios interceptor in /lib/api.js auto-appends ?country=... to /products
 * + /streams + /recommendations/for-you so the whole app filters to just
 * the sellers who ship there.
 */
function CountryHeaderPicker() {
  const [open, setOpen] = React.useState(false);
  const [country, setCountry] = React.useState(() => {
    try { return localStorage.getItem("buyer_country") || "ALL"; } catch { return "ALL"; }
  });
  const [countries, setCountries] = React.useState([]);
  const [q, setQ] = React.useState("");
  const rootRef = React.useRef(null);

  React.useEffect(() => {
    let mounted = true;
    import("../lib/api").then(({ api }) =>
      api.get("/countries").then(({ data }) => { if (mounted) setCountries(data); }).catch(() => {})
    );
    return () => { mounted = false; };
  }, []);

  React.useEffect(() => {
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = (iso2) => {
    setCountry(iso2);
    try { localStorage.setItem("buyer_country", iso2); } catch {}
    setOpen(false);
    setQ("");
    window.location.reload();
  };

  const sel = countries.find(c => c.iso2 === country);
  const filtered = q.trim()
    ? countries.filter(c => c.name.toLowerCase().includes(q.toLowerCase()))
    : countries;

  return (
    <div className="relative" ref={rootRef}>
      <button onClick={() => setOpen(o => !o)} data-testid="country-header-btn"
        className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#261534] border border-[#3D2845] hover:border-[#D4FF00] transition"
        aria-label="Shipping country">
        <span className="text-lg leading-none">{country === "ALL" ? "🌍" : (sel?.flag || "🌍")}</span>
        <span className="text-[11px] font-bold uppercase tracking-widest hidden sm:inline">
          {country === "ALL" ? "World" : country}
        </span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-[280px] bg-[#261534] border border-[#3D2845] rounded-2xl z-50 shadow-2xl overflow-hidden"
          data-testid="country-header-menu">
          <div className="p-2 border-b border-[#3D2845] flex items-center gap-2">
            <Search size={14} className="text-[#A1A1AA]"/>
            <input autoFocus className="bg-transparent outline-none text-sm w-full placeholder:text-[#A1A1AA]"
              placeholder="Search country…" value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="country-header-search"/>
          </div>
          <button onClick={() => choose("ALL")}
            data-testid="country-header-opt-ALL"
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#3D2845] transition ${country === "ALL" ? "bg-[#D4FF00]/10 text-[#D4FF00]" : ""}`}>
            <span className="text-lg">🌍</span>
            <span className="flex-1 text-left">Worldwide</span>
          </button>
          <div className="max-h-64 overflow-y-auto border-t border-[#3D2845]">
            {filtered.slice(0, 80).map(c => (
              <button key={c.iso2} onClick={() => choose(c.iso2)}
                data-testid={`country-header-opt-${c.iso2}`}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#3D2845] transition ${country === c.iso2 ? "bg-[#D4FF00]/10 text-[#D4FF00]" : ""}`}>
                <span className="text-lg">{c.flag}</span>
                <span className="flex-1 text-left truncate">{c.name}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-center text-sm text-[#A1A1AA]">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
